

































































































































































import {Vue, Component, Prop, Ref} from 'vue-property-decorator'
import {ComponentInfo, ComponentPayload, NGLComponentInfo, SceneShot} from '@/store/shots'
import {ParamStore} from '@/store/paramstore'
import {MolRepresentation, RepresentationSet} from '@/models'
import * as NGLViewer from 'ngl'
import {NGLRepresentationTypes} from '@/components/NGL/NGLRepresentationTypes'
import Params from '@/components/Params.vue'
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import SceneTemplateDialog from '@/components/NGL/SceneTemplateDialog.vue'
import MoleculesDialog from '@/components/NGL/MoleculesDialog.vue'
import NglComponentsList from '@/components/NGL/NglComponentsList.vue'
import {trackEvent} from '@/analytics'

@Component({
  name: 'NGLParamsDrawer',
  components: {
    SceneTemplateDialog,
    Params, VuePerfectScrollbar, MoleculesDialog,
    NglComponentsList,
  }
})
export default class NGLParamsDrawer extends Vue {
  @Prop() shot!: SceneShot
  @Prop() representationData!: { [index: string]: ParamStore }
  @Ref('compNameInput') readonly compNameInput!: any
  @Ref('repNameInput') readonly repNameInput!: any
  @Ref('nglCompList') readonly nglCompList!: any
  // Drawer open/close state
  drawerOpen = true

  representationsTypes: NGLViewer.StructureRepresentationType[] = NGLRepresentationTypes
  representationEdit = ''
  representationSelectedIndex = -1
  prevRepName = ''

  componentEdit = ''
  prevCompName = ''

  scrollbarSettings = {
    suppressScrollX: true
  }

  mounted() {
    if (this.currentComponentInfo && this.currentComponentInfo.representations.length)
      this.representationSelectedIndex = 0
  }

  toggleDrawer() {
    this.drawerOpen = !this.drawerOpen
  }

  // Item values for the component v-select
  get currentComponentInfo() {
    return this.currentComponentId ? this.shot.components.find(c => c.id === this.currentComponentId) : null
  }

  get currentComponentId(): string | null {
    return this.shot.currentComponent
  }

  set currentComponentId(id: string | null) {
    this.shot.setCurrentComponent(id)
  }

  get currentComponentIsStructure() {
    return this.currentComponentInfo?.type === 'structure'
  }

  get componentParamsStore(): ParamStore|null {
    return this.currentComponentInfo?.componentParams || null
  }

  get representations(): RepresentationSet {
    return this.currentComponentInfo?.representations || []
  }

  get representationSelected() {
    if (this.currentComponentId && this.representationSelectedIndex > -1) {
      const comp = this.shot.components.filter((c => c.id === this.currentComponentId))
      if (comp.length)
        return comp[0].representations[this.representationSelectedIndex] || false
    }
    return false
  }

  // Representation name editing
  showRepNameInput(repId: string) {
    return this.representationEdit && this.representationEdit === repId
  }

  editRepName(repId: string) {
    if (this.representationSelected) {
      this.prevRepName = this.representationSelected.name
      this.representationEdit = repId
      this.$nextTick(() => {
        this.repNameInput.focus()
      })
    }
  }

  resetRepNameEdit() {
    if (this.repNameInput.value === '') {
      this.cancelRepNameEdit()
    } else {
      this.prevRepName = ''
      this.representationEdit = ''
    }
  }

  updateRepName(newRepName: string) {
    const rep = this.currentComponentInfo?.representations.find(
      (v: MolRepresentation) => (this.representationEdit === v.id))
    if (rep && this.currentComponentId && newRepName)
      this.shot.updateRepresentationName(this.currentComponentId, rep.id, newRepName)
  }

  saveRepName() {
    if (this.repNameInput.value === '') {
      this.cancelRepNameEdit()
    } else {
      this.repNameInput.blur()
      this.prevRepName = ''
      this.representationEdit = ''
    }
  }

  cancelRepNameEdit() {
    if (this.prevRepName) {
      this.updateRepName(this.prevRepName)
      this.repNameInput.blur()
      this.prevRepName = ''
      this.representationEdit = ''
    }
  }

  showRepNameText(repId: string) {
    return this.representationEdit === '' || this.representationEdit !== repId
  }

  // Representations handling
  addRepresentation(rType: NGLViewer.StructureRepresentationType) {
    const name = this.getNextRepName(rType)
    if (!this.currentComponentId)
      return
    this.shot.addRepresentation(this.currentComponentId, rType, name)
    this.representationSelectedIndex = this.representations.length - 1
    if (this.currentComponentInfo)
      this.nglCompList.selectRepresentation(this.currentComponentInfo as ComponentInfo, this.representationSelectedIndex)
  }

  getNextRepName(rType: NGLViewer.StructureRepresentationType): string {
    const allRepsOfType = this.currentComponentInfo?.representations.filter(
      (rep => rep.kind === rType)).sort()
    let name = `${rType}`
    if (allRepsOfType && allRepsOfType.length)
      name += ` (${allRepsOfType.length})`

    return name
  }

  removeRepresentation(index: number) {
    if (this.currentComponentId)
      this.shot.removeRepresentation(this.currentComponentId, index)
  }

  onMoleculeRepresentationRemoved({componentId, index}: {componentId: string; index: number}) {
    console.log(`NGLParamsDrawer.removeRepresentation(${componentId}, ${index})`)
    if (this.shot.getComponentUuid(componentId))
      this.shot.removeRepresentation(componentId, index)

    if (componentId === this.currentComponentId) {
      let nextSelectedIndex = this.representationSelectedIndex

      if (index === this.representationSelectedIndex) {       // removing selected representation
        if (index === 0)
          nextSelectedIndex = index
        else
          nextSelectedIndex = index - 1
      } else if (index < this.representationSelectedIndex) {  // removing representation bellow selected
        nextSelectedIndex = this.representationSelectedIndex - 1
      }

      this.representationSelectedIndex = nextSelectedIndex
      this.nglCompList.selectRepresentation(this.currentComponentInfo as ComponentInfo, nextSelectedIndex)
    }
  }

  onComponentAdded(component: ComponentPayload) {
    this.shot.addComponentWithDefaults(component)
  }

  onComponentsAdded(components: ComponentPayload[]) {
    components.forEach((component: ComponentPayload) => this.shot.addComponentWithDefaults(component))
  }

  onComponentRemoved({component = '', index = 0}) {
    const comp = component as unknown as NGLComponentInfo
    const uuid = comp.uuid
    if (uuid && uuid !== '') {
      if (this.currentComponentId === uuid) {
        const otherCompIndex = this.shot.components.findIndex((component) => component.id !== uuid)
        if (otherCompIndex !== -1)
          this.shot.setCurrentComponent(this.shot.components[otherCompIndex].id)
        else
          this.shot.setCurrentComponent()
      }
      this.shot.removeComponent(uuid)
    }
  }

  onMoleculeRepresentationSelected({componentId = '', index = -1} = {}) {
    if (componentId && index > -1) {
      if (componentId !== this.currentComponentId)
        this.currentComponentId = componentId
      this.representationSelectedIndex = index
    }
  }

  // Component name editing
  get showCompNameText(): boolean {
    return this.componentEdit === ''
  }

  get showCompNameInput(): boolean {
    return this.componentEdit === this.currentComponentId
  }

  editCompName() {
    if (this.currentComponentId && this.currentComponentInfo && this.currentComponentInfo.name) {
      this.prevCompName = this.currentComponentInfo.name
      this.componentEdit = this.currentComponentId
      this.$nextTick(() => {
        this.compNameInput.focus()
      })
    }
  }

  resetCompNameEdit() {
    if (this.compNameInput.value === '') {
      this.cancelCompNameEdit()
    } else {
      this.prevCompName = ''
      this.componentEdit = ''
    }
  }

  updateCompName(compName: string) {
    const componentId = this.currentComponentId
    if (componentId)
      this.shot.updateComponentName(componentId, compName)
  }

  saveCompName() {
    if (this.compNameInput.value === '') {
      this.cancelCompNameEdit()
    } else {
      this.compNameInput.blur()
      this.prevCompName = ''
      this.componentEdit = ''
    }
  }

  cancelCompNameEdit() {
    if (this.prevCompName) {
      this.updateCompName(this.prevCompName)
      this.compNameInput.blur()
      this.prevCompName = ''
      this.componentEdit = ''
    }
  }

  doTrackEvent(event: string, data?: {[name: string]: string|number}) {
    return trackEvent(event, data)
  }
}
